import { FOLLOW_UP_TYPES, FOLLOW_UP_TYPE_LABELS, type CollectionFollowUpInput, type FollowUpType } from '@chukua/common';
import { Alert, DatePicker, Form, Input, Modal, Select } from 'antd';
import dayjs, { type Dayjs } from 'dayjs';
import type { CollectionFollowUpView } from '../follow-ups.api';
import { toContactedAt } from '../policies/follow-up-time';

interface FollowUpModalProps {
  open: boolean;
  /** Set when this entry corrects another: it starts from that entry's values and points at it. */
  corrects: CollectionFollowUpView | null;
  error: string | null;
  submitting: boolean;
  onSubmit: (input: CollectionFollowUpInput) => Promise<boolean>;
  onClose: () => void;
}

interface FormValues {
  type: FollowUpType;
  contactedAt: Dayjs;
  contactName?: string;
  outcome: string;
  notes?: string;
}

const initialValues = (corrects: CollectionFollowUpView | null): Partial<FormValues> => ({
  type: corrects?.type,
  contactedAt: corrects ? dayjs(corrects.contactedAt) : dayjs(),
  contactName: corrects?.contactName ?? undefined,
  outcome: corrects?.outcome,
  notes: corrects?.notes ?? undefined,
});

/** An entry is evidence, never edited: a correction is a new entry that keeps the old one on the timeline. */
export function FollowUpModal({ open, corrects, error, submitting, onSubmit, onClose }: FollowUpModalProps) {
  const [form] = Form.useForm<FormValues>();

  async function submit(values: FormValues) {
    const saved = await onSubmit({
      type: values.type,
      contactedAt: toContactedAt(values.contactedAt.toDate()),
      contactName: values.contactName,
      outcome: values.outcome,
      notes: values.notes,
      invoiceId: corrects?.invoiceId ?? undefined,
      correctsFollowUpId: corrects?.id,
    });
    if (saved) onClose();
  }

  return (
    <Modal
      open={open}
      onCancel={onClose}
      onOk={() => form.submit()}
      okText={corrects ? 'Record correction' : 'Record follow-up'}
      okButtonProps={{ loading: submitting }}
      title={corrects ? 'Correct a follow-up' : 'Record a follow-up'}
      destroyOnHidden
    >
      {error && <Alert className="mb-5" type="error" title={error} showIcon />}
      <Form<FormValues> form={form} layout="vertical" requiredMark={false} onFinish={submit} disabled={submitting} initialValues={initialValues(corrects)}>
        <Form.Item name="type" label="How" rules={[{ required: true, message: 'Choose how you got in touch' }]}>
          <Select placeholder="Select a type" options={FOLLOW_UP_TYPES.map((type) => ({ value: type, label: FOLLOW_UP_TYPE_LABELS[type] }))} />
        </Form.Item>
        <Form.Item name="contactedAt" label="When" rules={[{ required: true, message: 'Say when it happened' }]}>
          <DatePicker
            className="w-full"
            showTime={{ format: 'HH:mm' }}
            format="D MMM YYYY HH:mm"
            allowClear={false}
            disabledDate={(day) => day.isAfter(dayjs(), 'day')}
          />
        </Form.Item>
        <Form.Item name="contactName" label="Spoke to">
          <Input maxLength={200} />
        </Form.Item>
        <Form.Item name="outcome" label="Outcome" rules={[{ required: true, whitespace: true, message: 'Say what came of it' }]}>
          <Input maxLength={200} placeholder="e.g. Promised payment by Friday" />
        </Form.Item>
        <Form.Item name="notes" label="Notes">
          <Input.TextArea rows={3} maxLength={2000} />
        </Form.Item>
      </Form>
    </Modal>
  );
}
